import React, { useState } from 'react'
import { Grid, Button, makeStyles, Typography } from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'

import PasswordField from '../../form/PasswordField'
import PasswordRequirements from '../../form/PasswordRequirements'
import SecondaryButton from '../../form/SecondaryButton'
import MessageView from './messageView'

import { postToApi } from 'actions'
import { useStateValue } from 'context/store'
import { useResetPassFormValidation } from 'utils/customHooks/useResetPassFormValidation'
import { handleErrorMessage } from 'utils/customHooks/useApiRequest'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import { closeSpinnerAction, openSpinnerAction } from 'context/reducers/openSpinnerReducer'

const useChangePassFormStyles = makeStyles(() => ({
   changePasswordContainer: {
      height: '100%',
      width: 325
   }
}))

const CHANGE_PASSWORD_INITIAL_FORM_STATE = {
   currentPassword: '',
   newPassword: '',
   confirmPassword: ''
}

// NOTA: solo aplica para usuarios externos
function ChangePasswordForm({ handleClose, setParentView }) {
   const classes = useChangePassFormStyles()

   const [currentView, setCurrentView] = useState('formView')

   const [formData, setFormData] = useState(CHANGE_PASSWORD_INITIAL_FORM_STATE)

   return (
      <Grid className={classes.changePasswordContainer}>
         {currentView === 'formView' && (
            <FormView
               setCurrentView={setCurrentView}
               setParentView={setParentView}
               formData={formData}
               setFormData={setFormData}
            />
         )}
         {currentView === 'confirmView' && (
            <ConfirmView setCurrentView={setCurrentView} formData={formData} />
         )}
         {currentView === 'successMessageView' && (
            <MessageView
               handleClose={handleClose}
               title={'Cambiar Contraseña'}
               successMessage={'¡Contraseña Actualizada!'}
            />
         )}
         {currentView === 'errorMessageView' && (
            <MessageView
               error={true}
               setCurrentView={setCurrentView}
               title={'Cambiar Contraseña'}
            />
         )}
      </Grid>
   )
}

export default ChangePasswordForm

// ------------------------

const CHANGE_PASSWORD_INITIAL_INVALID_STATE = {
   currentPassword: { invalid: false, message: '' },
   newPassword: { invalid: false, message: '' },
   confirmPassword: { invalid: false, message: '' }
}

const useFormViewStyles = makeStyles(theme => ({
   formViewContainer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: theme.spacing(2)
   },
   titleContainer: {
      display: 'flex',
      marginBottom: theme.spacing(0.5)
   },
   title: {
      fontSize: 16,
      fontWeight: 'bold',
      margin: 'auto'
   },
   submitButton: {
      margin: theme.spacing(1, 0, 2, 0),
      textTransform: 'none',
      color: '#fff'
   },
   backButton: {
      cursor: 'pointer'
   }
}))

function FormView({ setCurrentView, setParentView, formData, setFormData }) {
   const classes = useFormViewStyles()

   const [isInvalid, setIsInvalid] = useState(
      CHANGE_PASSWORD_INITIAL_INVALID_STATE
   )

   const {
      setValue,
      isAnyPasswordFieldInvalid,
      showPassRequirements,
      setShowPassRequirements
   } = useResetPassFormValidation([formData, setFormData], setIsInvalid)

   function clean() {
      setFormData(CHANGE_PASSWORD_INITIAL_FORM_STATE)
      setIsInvalid(CHANGE_PASSWORD_INITIAL_INVALID_STATE)
      setShowPassRequirements(false)
   }

   function onSubmit() {
      if (!isAnyPasswordFieldInvalid()) {
         setCurrentView('confirmView')
      }
   }

   function onBack() {
      setParentView('profileView')
   }

   return (
      <Grid className={classes.formViewContainer}>
         <div className={classes.titleContainer}>
            <ArrowBackIcon className={classes.backButton} onClick={onBack} />
            <Typography className={classes.title}>
               Cambiar contraseña
            </Typography>
         </div>
         <Grid className={classes.container}>
            {/* NOTA: No se hace dentro de <form> pq la popup de MUI no evita el reload del submit así se le ponga preventDefault */}
            <PasswordField
               fieldLabel="Contraseña Actual *"
               fieldName="currentPassword"
               formValue={formData?.currentPassword}
               handleChange={setValue}
               fieldInvalid={isInvalid?.currentPassword?.isInvalid}
               errorMessage={
                  isInvalid?.currentPassword?.isInvalid &&
                  isInvalid?.currentPassword?.errorMessage
               }
            />
            <PasswordField
               fieldLabel="Nueva Contraseña *"
               fieldName="newPassword"
               formValue={formData?.newPassword}
               handleChange={setValue}
               fieldInvalid={isInvalid?.newPassword?.isInvalid}
               errorMessage={
                  isInvalid?.newPassword?.isInvalid &&
                  isInvalid?.newPassword?.errorMessage
               }
            />
            <PasswordField
               fieldLabel="Confirma tu Contraseña *"
               fieldName="confirmPassword"
               formValue={formData?.confirmPassword}
               handleChange={setValue}
               fieldInvalid={isInvalid?.confirmPassword?.isInvalid}
               errorMessage={
                  isInvalid?.confirmPassword?.isInvalid &&
                  isInvalid?.confirmPassword?.errorMessage
               }
            />
            {showPassRequirements && <PasswordRequirements />}
            <SecondaryButton text="LIMPIAR" onClick={clean} />
            <Button
               variant="contained"
               color="primary"
               className={classes.submitButton}
               fullWidth
               disableElevation
               onClick={onSubmit}
            >
               {'CAMBIAR CONTRASEÑA'}
            </Button>
         </Grid>
      </Grid>
   )
}
// --------------------------

const useConfirmViewStyles = makeStyles(theme => ({
   confirmViewContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(4)
   },
   iconContainer: {
      marginBottom: theme.spacing(3)
   },
   icon: {
      fontSize: 40
   },
   message: {
      fontSize: 14,
      textAlign: 'center',
      padding: theme.spacing(0, 3),
      marginBottom: theme.spacing(3)
   },
   buttonsContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-evenly'
   },
   button: {
      color: '#3264DC'
   }
}))

function ConfirmView({ setCurrentView, formData }) {
   const classes = useConfirmViewStyles()

   const [_, dispatch] = useStateValue()

   function onCancel() {
      setCurrentView('formView')
   }

   async function onAccept() {
      dispatch(openSpinnerAction())
      try {
         // NOTA: No aplica useApiRequest por callback en el catch
         const payload = { ...formData }
         const url = `${process.env.REACT_APP_AUTHORIZATION_API}/user/change/password`
         await postToApi(url, payload)
         setCurrentView('successMessageView')
      } catch (error) {
         dispatch(openSnackBarAction(`${handleErrorMessage(error)} (Cambiar Contraseña Externo: Enviar)`))
         setCurrentView('errorMessageView')
      } finally {
         dispatch(closeSpinnerAction())
      }
   }

   return (
      <Grid className={classes.confirmViewContainer}>
         <div className={classes.iconContainer}>
            <LockIcon className={classes.icon}></LockIcon>
         </div>
         <div className={classes.message}>
            ¿Estas seguro que deseas cambiar de contraseña?
         </div>
         <div className={classes.buttonsContainer}>
            <Button className={classes.button} onClick={onCancel}>
               CANCELAR
            </Button>
            <Button className={classes.button} onClick={onAccept}>
               ACEPTAR
            </Button>
         </div>
      </Grid>
   )
}
